import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';

export function BulkZipModal({ modal, onChange, onClose, onSubmit }) {
  return (
    <Dialog
      open={modal.open}
      onClose={() => { if (!modal.submitting) onClose(); }}
      fullWidth
      maxWidth='xs'
      PaperProps={{
        sx: {
          borderRadius: '18px',
          border: '1px solid #E8E8E8',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          overflow: 'visible',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3, pt: 3, pb: 2,
          borderBottom: '1px solid #F0F0F0',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40, height: 40, borderRadius: '12px',
            bgcolor: '#111111',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          }}
        >
          <TuneRoundedIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 15.5, color: '#111111' }}>
            Bulk ZIP Update
          </Typography>
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#8C8C8C', mt: 0.25 }}>
            Update should_contact for all leads in a ZIP
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {modal.result ? (
          /* ── Success result card ── */
          <Box
            sx={{
              borderRadius: '12px', border: '1px solid #D1FAE5', bgcolor: '#F0FDF4',
              p: 2.5, display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleRounded sx={{ fontSize: 20, color: '#10B981' }} />
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 14, color: '#065F46' }}>
                Update successful
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[
                { label: 'ZIP Code', value: modal.result.zip, valueColor: '#111111' },
                { label: 'Matched', value: modal.result.matched_count ?? '—', valueColor: '#111111' },
                { label: 'Modified', value: modal.result.modified_count ?? '—', valueColor: '#10B981' },
              ].map(({ label, value, valueColor }) => (
                <Box
                  key={label}
                  sx={{
                    flex: 1, borderRadius: '10px',
                    bgcolor: '#FFFFFF', border: '1px solid #D1FAE5',
                    p: 1.5, textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11, color: '#6B7280', mb: 0.25 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 15, color: valueColor }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.5, py: 1, borderRadius: '8px', alignSelf: 'flex-start',
                bgcolor: modal.result.should_contact ? '#ECFDF5' : '#F9FAFB',
                border: `1px solid ${modal.result.should_contact ? '#A7F3D0' : '#E5E7EB'}`,
              }}
            >
              {modal.result.should_contact
                ? <CheckRoundedIcon sx={{ fontSize: 14, color: '#10B981' }} />
                : <BlockRoundedIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
              }
              <Typography
                sx={{
                  fontFamily: 'Inter Bold, sans-serif', fontSize: 12, fontWeight: 700,
                  color: modal.result.should_contact ? '#065F46' : '#6B7280',
                }}
              >
                should_contact set to{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  {String(modal.result.should_contact)}
                </span>
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ── Form ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              size='small'
              label='ZIP Code'
              placeholder='e.g. 60616'
              value={modal.zip}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                onChange((prev) => ({ ...prev, zip: val, error: '' }));
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontFamily: 'Inter Regular, sans-serif',
                  fontSize: 14,
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#B0B8CC' },
                  '&.Mui-focused fieldset': { borderColor: '#111111', borderWidth: '1.5px' },
                },
                '& .MuiInputLabel-root': { fontFamily: 'Inter Regular, sans-serif', fontSize: 13 },
              }}
            />

            {/* Should contact selector */}
            <Box>
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#6B7280', mb: 1 }}>
                Set should_contact to
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[
                  { value: true, label: 'Should Contact', subLabel: 'true', Icon: CheckRoundedIcon, activeColor: '#10B981', activeBg: '#F0FDF4', activeBorder: '#A7F3D0' },
                  { value: false, label: 'Skip', subLabel: 'false', Icon: BlockRoundedIcon, activeColor: '#9CA3AF', activeBg: '#F3F4F6', activeBorder: '#E5E7EB' },
                ].map(({ value, label, subLabel, Icon }) => {
                  const isSelected = modal.shouldContact === value;
                  return (
                    <Box
                      key={String(value)}
                      onClick={() => onChange((prev) => ({ ...prev, shouldContact: value, error: '' }))}
                      sx={{
                        flex: 1, borderRadius: '10px',
                        border: `1.5px solid ${isSelected ? '#111111' : '#E5E7EB'}`,
                        bgcolor: isSelected ? '#111111' : '#FFFFFF',
                        p: 1.5,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
                        cursor: 'pointer',
                        transition: 'all 0.16s ease',
                        '&:hover': { borderColor: '#111111', bgcolor: isSelected ? '#111111' : '#F9FAFB' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: '8px',
                          bgcolor: isSelected ? '#FFFFFF1A' : (value ? '#F0FDF4' : '#F3F4F6'),
                          border: `1px solid ${isSelected ? '#FFFFFF33' : (value ? '#A7F3D0' : '#E5E7EB')}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Icon sx={{ fontSize: 17, color: isSelected ? '#FFFFFF' : (value ? '#10B981' : '#9CA3AF') }} />
                      </Box>
                      <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13, color: isSelected ? '#FFFFFF' : '#374151' }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11, color: isSelected ? '#FFFFFF99' : '#9CA3AF' }}>
                        {subLabel}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Info note */}
            <Box
              sx={{
                borderRadius: '8px', bgcolor: '#F9FAFB', border: '1px solid #F0F0F0',
                px: 1.5, py: 1,
                display: 'flex', alignItems: 'flex-start', gap: 0.75,
              }}
            >
              <SyncAltRoundedIcon sx={{ fontSize: 14, color: '#9CA3AF', mt: 0.15, flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#9CA3AF' }}>
                This will update <strong style={{ color: '#374151' }}>all leads</strong> matching the ZIP code.
                The change is immediate and cannot be undone from here.
              </Typography>
            </Box>

            {/* Error */}
            {modal.error ? (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 1, borderRadius: '8px',
                  bgcolor: '#FEF2F2', border: '1px solid #FECACA',
                }}
              >
                <CancelRounded sx={{ fontSize: 15, color: '#DC2626', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#DC2626' }}>
                  {modal.error}
                </Typography>
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button
          disabled={modal.submitting}
          onClick={onClose}
          sx={{
            textTransform: 'none', color: '#6B7280',
            fontFamily: 'Inter Regular, sans-serif', fontSize: 13,
            borderRadius: '8px', px: 2,
            '&:hover': { bgcolor: '#F3F4F6' },
          }}
        >
          {modal.result ? 'Close' : 'Cancel'}
        </Button>

        {!modal.result && (
          <Button
            disabled={modal.submitting || !modal.zip.trim()}
            onClick={onSubmit}
            variant='contained'
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13,
              borderRadius: '8px', bgcolor: '#111111', boxShadow: 'none', px: 2.5, flex: 1,
              '&:hover': { bgcolor: '#333333', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#E8E8E8', color: '#B0B0B0', boxShadow: 'none' },
            }}
          >
            {modal.submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: '#8C8C8C' }} />
                Updating…
              </Box>
            ) : (
              'Apply Bulk Update'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
